import { useEffect, useState } from 'react';
import { Bot, Crown, Headset, UserRound, Zap } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import BotAvatar, { avatarGlyph } from '../agents/BotAvatar';

/**
 * Who sent a message, drawn as a small avatar beside the bubble.
 *
 * Outbound: the stamped sender (bot face, resource photo/initials, owner,
 * automation, phone agent, system process on the business logo). Older rows
 * without a stamp fall back to the AI flag or a generic team avatar. Inbound:
 * the customer's initials.
 */

export interface MessageSender {
  kind: 'resource' | 'owner' | 'user' | 'automation' | 'bot' | 'phone_agent' | 'system' | string;
  id: string | null;
  name: string | null;
}

interface BotInfo { id: string; name: string; glyph: string; colour: string; is_master: boolean; kind: 'specialist' | 'website' }
interface ResourceInfo { id: string; name: string; avatar_url: string | null; auth_user_id: string | null }
interface BusinessInfo { name: string; logo_url: string | null }
interface Directory { bots: Map<string, BotInfo>; resources: Map<string, ResourceInfo>; resourcesByUser: Map<string, ResourceInfo>; business: BusinessInfo | null }

/**
 * Sender for a message row, whichever shape it arrived in: the thread call
 * nests it as `sender`, while fresh inserts and realtime rows carry the flat
 * sender_kind / sender_id / sender_name columns.
 */
export function senderOf(m: any): MessageSender | null {
  if (!m) return null;
  if (m.sender && typeof m.sender === 'object' && m.sender.kind) return m.sender as MessageSender;
  if (m.sender_kind) return { kind: String(m.sender_kind), id: m.sender_id ?? null, name: m.sender_name ?? null };
  return null;
}

const DIRECTORY_TTL_MS = 5 * 60 * 1000;
const directoryCache = new Map<string, { at: number; value: Directory; promise?: Promise<Directory> }>();
const listeners = new Set<() => void>();

async function loadDirectory(businessId: string): Promise<Directory> {
  const empty: Directory = { bots: new Map(), resources: new Map(), resourcesByUser: new Map(), business: null };
  if (!supabase) return empty;
  const [botsRes, resRes, bizRes] = await Promise.all([
    supabase.from('agent_bots').select('id, name, glyph, colour, is_master, kind').eq('business_id', businessId),
    supabase.from('resources').select('id, first_name, last_name, avatar_url, auth_user_id').eq('business_id', businessId),
    supabase.from('businesses').select('name, booking_settings').eq('id', businessId).maybeSingle(),
  ]);
  const dir = empty;
  if (bizRes.data) {
    const biz: any = bizRes.data;
    dir.business = { name: biz.name || 'Business', logo_url: biz.booking_settings?.logo_url || null };
  }
  for (const b of (botsRes.data || []) as any[]) {
    dir.bots.set(b.id, { id: b.id, name: b.name || 'AI', glyph: b.glyph || 'triangle', colour: b.colour || '#a3e635', is_master: !!b.is_master, kind: b.kind || '' });
  }
  for (const r of (resRes.data || []) as any[]) {
    const info: ResourceInfo = { id: r.id, name: `${r.first_name || ''} ${r.last_name || ''}`.trim(), avatar_url: r.avatar_url || null, auth_user_id: r.auth_user_id || null };
    dir.resources.set(r.id, info);
    if (info.auth_user_id) dir.resourcesByUser.set(info.auth_user_id, info);
  }
  return dir;
}

function useSenderDirectory(businessId: string | undefined): Directory | null {
  const [, force] = useState(0);
  useEffect(() => {
    if (!businessId) return;
    const cached = directoryCache.get(businessId);
    if (cached && Date.now() - cached.at < DIRECTORY_TTL_MS) return;
    if (cached?.promise) return;
    const promise = loadDirectory(businessId).then((value) => {
      directoryCache.set(businessId, { at: Date.now(), value });
      listeners.forEach((l) => l());
      return value;
    });
    directoryCache.set(businessId, { at: 0, value: cached?.value || { bots: new Map(), resources: new Map(), resourcesByUser: new Map(), business: null }, promise });
  }, [businessId]);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  if (!businessId) return null;
  return directoryCache.get(businessId)?.value || null;
}

function initialsOf(name: string | null | undefined, fallback = '?'): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return fallback;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Circle({ title, className, children, size }: { title: string; className: string; children: React.ReactNode; size: number }) {
  return (
    <span
      title={title}
      className={`inline-flex shrink-0 items-center justify-center rounded-full ring-2 ring-white overflow-hidden ${className}`}
      style={{ width: size, height: size }}
    >
      {children}
    </span>
  );
}

export default function SenderBadge({
  businessId,
  sender,
  direction,
  aiResponse,
  isPhoneAgent,
  customerName,
  size = 22,
}: {
  businessId: string | undefined;
  sender?: MessageSender | null;
  direction: 'inbound' | 'outbound';
  aiResponse?: boolean;
  /** Call handled by the AI phone agent (call_history.agent_id set). */
  isPhoneAgent?: boolean;
  customerName?: string | null;
  size?: number;
}) {
  const dir = useSenderDirectory(businessId);
  const iconSize = Math.round(size * 0.55);

  if (direction === 'inbound') {
    const label = customerName?.trim() || 'Customer';
    return (
      <Circle title={label} className="bg-gray-200 text-gray-700" size={size}>
        {customerName?.trim()
          ? <span className="font-semibold leading-none" style={{ fontSize: Math.round(size * 0.42) }}>{initialsOf(customerName)}</span>
          : <UserRound size={iconSize} />}
      </Circle>
    );
  }

  const kind = sender?.kind || (isPhoneAgent ? 'phone_agent' : aiResponse ? 'bot' : null);

  if (kind === 'bot') {
    const bot = sender?.id ? dir?.bots.get(sender.id) : undefined;
    const title = bot?.name || sender?.name || 'AI';
    if (bot) {
      return (
        <span title={title} className="inline-flex shrink-0 rounded-full ring-2 ring-white bg-zinc-900" style={{ width: size, height: size, padding: 2 }}>
          <BotAvatar glyph={avatarGlyph(bot)} colour={bot.colour} size={size - 4} />
        </span>
      );
    }
    return (
      <Circle title={title} className="bg-violet-600 text-white" size={size}><Bot size={iconSize} /></Circle>
    );
  }

  if (kind === 'resource') {
    const res = sender?.id ? dir?.resources.get(sender.id) : undefined;
    const name = res?.name || sender?.name || 'Team member';
    if (res?.avatar_url) {
      return (
        <Circle title={name} className="bg-gray-300" size={size}>
          <img src={res.avatar_url} alt={name} className="h-full w-full object-cover" />
        </Circle>
      );
    }
    return (
      <Circle title={name} className="bg-indigo-600 text-white" size={size}>
        <span className="font-semibold leading-none" style={{ fontSize: Math.round(size * 0.42) }}>{initialsOf(name, 'TM')}</span>
      </Circle>
    );
  }

  if (kind === 'owner' || kind === 'user' || kind === 'system') {
    // An admin with a linked resource is shown as that resource.
    const res = kind !== 'system' && sender?.id ? dir?.resourcesByUser.get(sender.id) : undefined;
    if (res) {
      if (res.avatar_url) {
        return (
          <Circle title={res.name} className="bg-gray-300" size={size}>
            <img src={res.avatar_url} alt={res.name} className="h-full w-full object-cover" />
          </Circle>
        );
      }
      return (
        <Circle title={res.name} className="bg-indigo-600 text-white" size={size}>
          <span className="font-semibold leading-none" style={{ fontSize: Math.round(size * 0.42) }}>{initialsOf(res.name, 'TM')}</span>
        </Circle>
      );
    }
    // Otherwise the business itself sent it: show the business logo. System
    // sends (receipts, order emails, queue texts) name the process that sent them.
    const biz = dir?.business;
    const title = kind === 'system'
      ? `${biz?.name || 'Business'} · ${sender?.name || 'System'}`
      : `${biz?.name || 'Business'} (admin)`;
    if (biz?.logo_url) {
      // Logos are drawn for the dark app header, so keep them on a dark disc.
      return (
        <Circle title={title} className="bg-gray-900 p-[3px]" size={size}>
          <img src={biz.logo_url} alt={biz.name} className="h-full w-full object-contain" />
        </Circle>
      );
    }
    if (biz?.name) {
      return (
        <Circle title={title} className="bg-gray-900 text-white" size={size}>
          <span className="font-semibold leading-none" style={{ fontSize: Math.round(size * 0.42) }}>{initialsOf(biz.name, 'B')}</span>
        </Circle>
      );
    }
    return (
      <Circle title={title} className="bg-gray-900 text-white" size={size}><Crown size={iconSize} /></Circle>
    );
  }

  if (kind === 'automation') {
    return (
      <Circle title={`Automation · ${sender?.name || 'Automation'}`} className="bg-emerald-600 text-white" size={size}><Zap size={iconSize} /></Circle>
    );
  }

  if (kind === 'phone_agent') {
    return (
      <Circle title={sender?.name || 'AI phone agent'} className="bg-sky-600 text-white" size={size}><Headset size={iconSize} /></Circle>
    );
  }

  // Older rows with no stamp.
  return (
    <Circle title="Team" className="bg-gray-400 text-white" size={size}><UserRound size={iconSize} /></Circle>
  );
}
